<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue';
import { Tooltip, Segmented, message } from 'ant-design-vue';
import { WalletOutlined, ReloadOutlined } from '@ant-design/icons-vue';
import type { TeamModeValue } from '@shared/types';
import { useRechargeModal } from '@/composables/useRechargeModal';
import { useIdentityCredits } from '@/composables/useIdentityCredits';

const {
  creditsStore,
  teamStore,
  displayBalance,
  displayLoading,
  isDisplayBalanceReady,
  refreshIdentityCredits,
} = useIdentityCredits();
const { openRechargeModal } = useRechargeModal();

let debounceTimer: ReturnType<typeof setTimeout> | null = null;

const modeOptions = [
  { label: '个人', value: 0 },
  { label: '团队', value: 1 },
];

const tooltipTitle = computed(() => (
  isDisplayBalanceReady.value ? '点击充值' : '获取余额失败，点击重试'
));

const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    refreshIdentityCredits();
  }
};

onMounted(() => {
  refreshIdentityCredits();
  document.addEventListener('visibilitychange', handleVisibilityChange);
});

onUnmounted(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
});

const handleClick = () => {
  openRechargeModal();
};

const handleRetry = (e: Event) => {
  e.stopPropagation();
  refreshIdentityCredits();
};

const handleMouseEnter = () => {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }

  debounceTimer = setTimeout(() => {
    refreshIdentityCredits();
    debounceTimer = null;
  }, 500);
};

const handleModeChange = async (value: string | number) => {
  try {
    await teamStore.switchMode(Number(value) as TeamModeValue);
    await creditsStore.refreshBalance();
  } catch (error) {
    message.error(error instanceof Error ? error.message : '切换团队身份失败');
  }
};
</script>

<template>
  <div class="credits-container" @mouseenter="handleMouseEnter">
    <Segmented
      v-if="teamStore.hasTeam"
      class="team-mode-switch"
      size="small"
      :value="teamStore.useTeamMode"
      :options="modeOptions"
      :disabled="teamStore.switching"
      @change="handleModeChange"
    />

    <div class="credits-display">
      <WalletOutlined />
      <span v-if="displayLoading" class="credits-value">...</span>
      <span v-else-if="displayBalance >= 0" class="credits-value">{{ displayBalance }}</span>
      <span v-else class="credits-value error">
        --
        <ReloadOutlined class="retry-icon" @click="handleRetry" />
      </span>
    </div>

    <Tooltip :title="tooltipTitle">
      <div
        class="recharge-btn"
        @click="isDisplayBalanceReady ? handleClick() : handleRetry($event)"
      >
        充值
      </div>
    </Tooltip>
  </div>
</template>

<style lang="scss" scoped>
.credits-container {
  display: flex;
  align-items: center;
  height: 34px;
}

.team-mode-switch {
  flex: 0 0 auto;
  margin-right: 8px;
  height: 34px;
  padding: 2px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 17px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
  transition: background-color 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.18);
  }

  :deep(.ant-segmented-group) {
    height: 28px;
    align-items: center;
  }

  :deep(.ant-segmented-item) {
    min-width: 44px;
    height: 28px;
    border-radius: 14px;
    color: rgba(255, 255, 255, 0.62);
    transition: color 0.2s ease, background-color 0.2s ease;

    &:not(.ant-segmented-item-selected):hover {
      color: rgba(255, 255, 255, 0.88);
      background: rgba(255, 255, 255, 0.06);
    }
  }

  :deep(.ant-segmented-item-label) {
    min-height: 28px;
    line-height: 28px;
    padding: 0 10px;
    font-size: 13px;
    font-weight: 500;
  }

  :deep(.ant-segmented-thumb),
  :deep(.ant-segmented-item-selected) {
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.16);
    box-shadow:
      0 2px 8px rgba(0, 0, 0, 0.18),
      inset 0 0 0 1px rgba(255, 255, 255, 0.08);
  }

  :deep(.ant-segmented-item-selected) {
    color: #ffffff;
  }

  :deep(.ant-segmented-item-disabled) {
    opacity: 0.55;
  }
}

.credits-display {
  display: flex;
  align-items: center;
  height: 100%;
  min-width: 76px;
  padding: 0 12px;
  background-color: var(--bg-tertiary, #2a2a2a);
  border-radius: 17px 0 0 17px;
  color: var(--text-secondary, #999);
  font-size: 14px;
  border: 1px solid var(--border-color, #3a3a3a);
  border-right: none;

  .credits-value {
    margin-left: 6px;
    color: var(--color-warning, #faad14);
    font-weight: 500;
    white-space: nowrap;

    &.error {
      color: var(--text-tertiary, #666);
      display: flex;
      align-items: center;
      gap: 4px;
    }
  }

  .retry-icon {
    font-size: 12px;
    color: var(--color-primary, #0066ff);
    cursor: pointer;
  }
}

.recharge-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-width: 52px;
  padding: 0 14px;
  background-color: var(--bg-tertiary, #2a2a2a);
  border-radius: 0 17px 17px 0;
  color: var(--text-secondary, #999);
  font-size: 14px;
  cursor: pointer;
  border: 1px solid var(--border-color, #3a3a3a);
  transition: all 0.2s;

  &:hover {
    background-color: var(--bg-hover, #3a3a3a);
    color: var(--text-primary, #fff);
  }
}
</style>
